import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Zap, Table2, Tag, Lock } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { ADDONS } from "@/data/siteData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveImageUrl = (url) => (url && url.startsWith("/api") ? `${BACKEND_URL}${url}` : url);

const DateCard = ({ event, taken, selection, onSelectStall, onToggleAddon }) => {
  const stallNumbers = Array.from({ length: event.total_stalls }, (_, i) => i + 1);

  return (
    <div className="rounded-2xl border border-gray-200 p-4 sm:p-6 bg-[#FDFBF7]">
      <p className="font-bold text-xl mb-1">{event.date_label}</p>
      <p className="text-base text-gray-500 mb-5">₹{event.price_per_day.toLocaleString("en-IN")}/day · {event.venue_name}</p>

      {event.bulk_discount_amount > 0 && event.bulk_discount_min_stalls >= 2 && (
        <p className="text-sm text-[#D81B60] bg-[#FDF2F5] rounded-lg px-3 py-2 mb-5 font-mono flex items-center gap-1.5" data-testid={`bulk-discount-hint-${event.date}`}>
          <Tag size={14} /> Book {event.bulk_discount_min_stalls}+ stalls here and save ₹{event.bulk_discount_amount.toLocaleString("en-IN")}
        </p>
      )}

      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <label className="text-sm font-mono uppercase tracking-wide text-gray-400 mb-2 block">Select your stall</label>
          <div className="grid grid-cols-6 gap-1.5" data-testid={`stall-grid-${event.date}`}>
            {stallNumbers.map((n) => {
              const isTaken = taken.includes(n);
              const isSelected = selection?.stall_number === n;
              return (
                <button
                  key={n}
                  type="button"
                  disabled={isTaken}
                  onClick={() => onSelectStall(n)}
                  data-testid={`stall-number-${event.date}-${n}`}
                  title={isTaken ? `Stall ${n} — Occupied` : `Stall ${n}`}
                  className={`aspect-square rounded-lg text-sm sm:text-base font-bold flex items-center justify-center transition-all duration-200 ${
                    isTaken
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : isSelected
                      ? "bg-[#D81B60] text-white scale-110 shadow-lg shadow-[#D81B60]/40 z-10"
                      : "bg-white border-2 border-gray-300 text-[#1A1A1A] hover:border-[#D81B60] hover:scale-105"
                  }`}
                >
                  {isTaken ? <Lock size={13} /> : n}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-white border-2 border-gray-300 inline-block" /> Free</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#D81B60] inline-block" /> Selected</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-gray-200 inline-block" /> Booked</span>
          </div>
        </div>

        {event.venue_image_url && (
          <div>
            <label className="text-sm font-mono uppercase tracking-wide text-gray-400 mb-2 block">Venue map (for reference)</label>
            <img
              src={resolveImageUrl(event.venue_image_url)}
              alt={`${event.venue_name} stall map`}
              data-testid={`venue-image-${event.date}`}
              className="w-full rounded-xl border border-gray-200 object-contain max-h-[420px] bg-white"
            />
          </div>
        )}
      </div>

      {selection && (
        <div className="mt-5 pt-5 border-t border-gray-200 space-y-2">
          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-base flex items-center gap-2"><Zap size={16} className="text-[#E8A33D]" /> Electrical Connection (+₹{ADDONS.electricity.price})</span>
            <input type="checkbox" data-testid={`addon-electricity-${event.date}`} checked={!!selection.electricity} onChange={() => onToggleAddon("electricity")} className="w-4 h-4 accent-[#D81B60]" />
          </label>
          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-base flex items-center gap-2"><Table2 size={16} className="text-[#3F51B5]" /> Extra Table (+₹{ADDONS.extraTable.price})</span>
            <input type="checkbox" data-testid={`addon-extra-table-${event.date}`} checked={!!selection.extra_table} onChange={() => onToggleAddon("extra_table")} className="w-4 h-4 accent-[#D81B60]" />
          </label>
        </div>
      )}
    </div>
  );
};

export const StallSelection = ({ data, setData, onNext }) => {
  const navigate = useNavigate();
  const selectedDates = data.selected_dates || [];
  const stallSelections = data.stall_selections || [];
  const [events, setEvents] = useState([]);
  const [takenByDate, setTakenByDate] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/events/upcoming`),
      axios.get(`${API}/vendor/taken-stalls`),
    ])
      .then(([evRes, takenRes]) => {
        setEvents(evRes.data || []);
        setTakenByDate(takenRes.data.taken || {});
      })
      .catch(() => toast.error("Could not load upcoming markets. Please refresh."))
      .finally(() => setLoading(false));
  }, []);

  const eventByDate = (date) => events.find((e) => e.date === date);

  const toggleDate = (date) => {
    setData((d) => {
      const dates = d.selected_dates || [];
      if (dates.includes(date)) {
        return { ...d, selected_dates: dates.filter((x) => x !== date), stall_selections: (d.stall_selections || []).filter((s) => s.date !== date) };
      }
      return { ...d, selected_dates: [...dates, date] };
    });
  };

  const selectStall = (date, num) => {
    if ((takenByDate[date] || []).includes(num)) return;
    setData((d) => {
      const others = (d.stall_selections || []).filter((s) => s.date !== date);
      const current = (d.stall_selections || []).find((s) => s.date === date);
      if (current && current.stall_number === num) {
        return { ...d, stall_selections: others };
      }
      return { ...d, stall_selections: [...others, { date, stall_number: num, electricity: current?.electricity || false, extra_table: current?.extra_table || false }] };
    });
  };

  const toggleAddon = (date, key) => {
    setData((d) => ({
      ...d,
      stall_selections: (d.stall_selections || []).map((s) => (s.date === date ? { ...s, [key]: !s[key] } : s)),
    }));
  };

  const { total, discount } = (() => {
    const groups = {};
    stallSelections.forEach((s) => {
      const ev = eventByDate(s.date);
      if (!ev) return;
      const line = ev.price_per_day + (s.electricity ? ADDONS.electricity.price : 0) + (s.extra_table ? ADDONS.extraTable.price : 0);
      const g = groups[ev.venue_name] || { subtotal: 0, count: 0, event: ev };
      g.subtotal += line;
      g.count += 1;
      groups[ev.venue_name] = g;
    });
    let totalAmt = 0;
    let discountAmt = 0;
    Object.values(groups).forEach((g) => {
      let subtotal = g.subtotal;
      if (g.event.bulk_discount_min_stalls >= 2 && g.count >= g.event.bulk_discount_min_stalls && g.event.bulk_discount_amount > 0) {
        const d = Math.min(g.event.bulk_discount_amount, subtotal);
        subtotal -= d;
        discountAmt += d;
      }
      totalAmt += subtotal;
    });
    return { total: totalAmt, discount: discountAmt };
  })();

  const handleNext = () => {
    if (selectedDates.length === 0) {
      toast.error("Please select at least one market date.");
      return;
    }
    const missing = selectedDates.some((date) => !stallSelections.find((s) => s.date === date));
    if (missing) {
      toast.error("Please select a stall for each chosen date.");
      return;
    }
    onNext();
  };

  if (loading) {
    return (
      <div data-testid="screen-1-loading" className="text-center py-20">
        <p className="text-gray-400 font-mono text-sm">Loading upcoming markets...</p>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div data-testid="screen-1-no-events" className="text-center py-20 max-w-md mx-auto">
        <p className="text-gray-500 mb-6">No markets are currently open for booking. Please check back soon.</p>
        <button onClick={() => navigate("/book-a-stall")} data-testid="no-events-back-button" className="rounded-full border border-gray-300 px-6 py-3 text-sm">Back</button>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} data-testid="screen-1-stall-selection" className="max-w-5xl mx-auto">
      <h2 className="font-extrabold text-3xl sm:text-4xl text-center mb-2">Choose your dates & stall</h2>
      <p className="text-gray-500 text-base text-center mb-8">Pricing varies by market date — see each date below.</p>

      <div className="grid grid-cols-2 gap-3 mb-10 max-w-md mx-auto">
        {events.map((event) => {
          const isSelected = selectedDates.includes(event.date);
          return (
            <button
              key={event.date}
              type="button"
              onClick={() => toggleDate(event.date)}
              data-testid={`stall-date-option-${event.date}`}
              className={`flex items-center justify-between rounded-xl border-2 px-4 py-3 text-left transition-all duration-300 ${
                isSelected ? "border-[#D81B60] bg-[#FDF2F5]" : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <div>
                <p className="text-base font-semibold">{event.date_label}</p>
                <p className="text-sm text-gray-400">₹{event.price_per_day.toLocaleString("en-IN")}/day</p>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? "border-[#D81B60] bg-[#D81B60]" : "border-gray-300"}`}>
                {isSelected && <Check size={12} className="text-white" />}
              </div>
            </button>
          );
        })}
      </div>

      {selectedDates.length > 0 && (
        <div className="space-y-8 mb-10">
          {selectedDates.map((date) => {
            const event = eventByDate(date);
            if (!event) return null;
            return (
              <DateCard
                key={date}
                event={event}
                taken={takenByDate[date] || []}
                selection={stallSelections.find((s) => s.date === date)}
                onSelectStall={(num) => selectStall(date, num)}
                onToggleAddon={(key) => toggleAddon(date, key)}
              />
            );
          })}
        </div>
      )}

      {stallSelections.length > 0 && (
        <div data-testid="price-summary" className="rounded-2xl bg-[#1A1A1A] text-white p-6 mb-8">
          {discount > 0 && (
            <p className="text-sm text-[#F48FB1] font-mono mb-2" data-testid="bulk-discount-line">
              <Tag size={13} className="inline mr-1" /> Bulk discount applied: −₹{discount.toLocaleString("en-IN")}
            </p>
          )}
          <p className="text-sm uppercase tracking-[0.15em] text-white/50 font-mono mb-2">Total</p>
          <p className="text-3xl font-extrabold text-[#F48FB1]">₹{total.toLocaleString("en-IN")}</p>
        </div>
      )}

      <div className="flex justify-between gap-4">
        <button onClick={() => navigate("/book-a-stall")} data-testid="stall-selection-back-button" className="rounded-full border border-gray-300 px-6 py-3 text-sm">Back</button>
        <button onClick={handleNext} data-testid="stall-selection-continue-button" className="rounded-full bg-[#D81B60] text-white px-8 py-3 text-sm hover:bg-[#AD144B] transition-colors duration-300">
          Continue
        </button>
      </div>
    </motion.div>
  );
};
